import { useEffect, useState } from 'react'
import { RefreshCw } from 'lucide-react'
import { PageHeader } from '~/components/PageHeader'
import { Button } from '~/components/ui/button'
import { Toggle } from '~/components/ui/toggle'
import { cn } from '~/lib/utils'
import {
    PERIODS,
    PERIOD_LABEL,
    type View,
} from '~/components/dashboard/constants'

// "n초 전 갱신" 라벨 — 1초마다 다시 그려 경과 시간을 갱신한다.
function useAgo(lastUpdated: number | null): string {
    const [, tick] = useState(0)
    useEffect(() => {
        if (lastUpdated === null) return
        const id = window.setInterval(() => tick((n) => n + 1), 1000)
        return () => window.clearInterval(id)
    }, [lastUpdated])
    if (lastUpdated === null) return '갱신 대기'
    const sec = Math.floor((Date.now() - lastUpdated) / 1000)
    if (sec < 3) return '방금 갱신'
    if (sec < 60) return `${sec}초 전 갱신`
    return `${Math.floor(sec / 60)}분 전 갱신`
}

// 세그먼트 토글 [통계 | 시계열].
function SegmentToggle({
    value,
    onChange,
}: {
    value: View
    onChange: (v: View) => void
}) {
    const items: { key: View; label: string }[] = [
        { key: 'stats', label: '통계' },
        { key: 'timeseries', label: '시계열' },
    ]
    return (
        <div
            role='tablist'
            aria-label='대시보드 보기 전환'
            className='border-border bg-secondary inline-flex rounded-[6px] border p-0.5'>
            {items.map((it) => (
                <button
                    key={it.key}
                    type='button'
                    role='tab'
                    aria-selected={value === it.key}
                    onClick={() => onChange(it.key)}
                    className={cn(
                        'focus-visible:ring-ring rounded-[4px] px-3.5 py-1.5 text-sm font-bold transition-colors outline-none focus-visible:ring-2',
                        value === it.key
                            ? 'bg-card text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground'
                    )}>
                    {it.label}
                </button>
            ))}
        </div>
    )
}

// 기간 선택 [7일·30일·90일].
function PeriodSelect({
    value,
    onChange,
}: {
    value: number
    onChange: (v: number) => void
}) {
    return (
        <div
            role='group'
            aria-label='기간 선택'
            className='border-border inline-flex overflow-hidden rounded-[6px] border'>
            {PERIODS.map((p) => (
                <button
                    key={p}
                    type='button'
                    aria-pressed={value === p}
                    onClick={() => onChange(p)}
                    className={cn(
                        'focus-visible:ring-ring border-border px-3 py-1.5 text-sm transition-colors not-first:border-l focus-visible:ring-2 focus-visible:outline-none',
                        value === p
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-card text-muted-foreground hover:bg-secondary'
                    )}>
                    {PERIOD_LABEL[p]}
                </button>
            ))}
        </div>
    )
}

// 헤더 + 우상단 컨트롤(보기 토글 · 기간 · 자동/수동 새로고침).
export function DashboardHeader({
    view,
    onViewChange,
    days,
    onDaysChange,
    autoRefresh,
    onAutoRefreshChange,
    lastUpdated,
    onRefresh,
}: {
    view: View
    onViewChange: (v: View) => void
    days: number
    onDaysChange: (v: number) => void
    autoRefresh: boolean
    onAutoRefreshChange: (v: boolean) => void
    lastUpdated: number | null
    onRefresh: () => void
}) {
    const ago = useAgo(lastUpdated)
    return (
        <div className='mb-5 flex flex-wrap items-start justify-between gap-4'>
            <PageHeader
                title='운영 대시보드'
                description='서비스 전반의 사용 현황 한눈에 보기'
            />
            {/* 우상단 컨트롤: 보기 토글 · 기간 · 자동 새로고침 · 수동 새로고침 */}
            <div className='flex flex-wrap items-center gap-3'>
                <SegmentToggle value={view} onChange={onViewChange} />
                {view === 'timeseries' ? (
                    <PeriodSelect value={days} onChange={onDaysChange} />
                ) : null}
                <div className='flex items-center gap-2'>
                    <Toggle
                        checked={autoRefresh}
                        onCheckedChange={onAutoRefreshChange}
                        aria-label='자동 새로고침'
                    />
                    <span className='text-muted-foreground flex items-center gap-1.5 text-[13px] tabular-nums'>
                        {autoRefresh && lastUpdated !== null ? (
                            <span className='bg-success inline-block size-1.5 animate-pulse rounded-full' />
                        ) : null}
                        {ago}
                    </span>
                </div>
                <Button
                    variant='tertiary'
                    size='sm'
                    onClick={() => void onRefresh()}
                    aria-label='수동 새로고침'>
                    <RefreshCw className='size-4' />
                    새로고침
                </Button>
            </div>
        </div>
    )
}
